import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { ReactNode } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { OrderSummaryTestIds } from '../../../../../../utils/testIds/OrderSummary.testIds';
import RachelChangedValue from '../../../../../rachel/components/RachelChangedValue';
import { useTranslation } from '../../../../../translation/TranslationContext';
import HideValueTag from './components/HideValueTag';
import ScreenReaderLabel from './components/ScreenReaderLabel';
import useOrderSummaryTotalValue from './hooks/useOrderSummaryTotalValue';

interface OrderSummaryTotalValueProps {
  testId: OrderSummaryTestIds | `${OrderSummaryTestIds}-${number}`;
  labelText?: string;
  srLabel?: LangEnum;
  tooltip?: LangEnum;
  value?: number;
  isStrikethrough?: boolean;
  isStacked?: boolean;
  stackedLabel?: LangEnum;
  labelAdornment?: ReactNode;
}

const OrderSummaryTotalValue = ({
  testId,
  labelText,
  srLabel,
  tooltip,
  value = 0,
  isStrikethrough,
  isStacked,
  stackedLabel,
  labelAdornment,
}: OrderSummaryTotalValueProps) => {
  const { translate } = useTranslation();
  const {
    shouldHideOrderValues,
    direction,
    alignItems,
    visibleLabel,
    tooltipLabel,
    textDecoration,
    valueVisibility,
    formattedValue,
  } = useOrderSummaryTotalValue({
    labelText,
    tooltip,
    stackedLabel,
    value,
    isStrikethrough,
    isStacked,
  });

  return (
    <Tooltip TooltipContent={tooltipLabel && translate(tooltipLabel)} placement="top">
      <Stack direction={direction} sx={{ alignItems, columnGap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', position: 'relative' }}>
          {srLabel && !visibleLabel && <ScreenReaderLabel label={srLabel} />}
          <Typography
            noWrap
            testId={testId}
            variant="body2"
            sx={{ visibility: valueVisibility, textDecoration }}
          >
            <RachelChangedValue value={value}>{formattedValue}</RachelChangedValue>
          </Typography>
          {shouldHideOrderValues && <HideValueTag />}
        </Box>

        {visibleLabel && (
          <Stack direction="row" sx={{ alignItems: 'center', columnGap: 0.5 }}>
            <Typography noWrap variant="body2" sx={{ color: 'inherit' }}>
              {visibleLabel}
            </Typography>
            {labelAdornment}
          </Stack>
        )}
      </Stack>
    </Tooltip>
  );
};

export default OrderSummaryTotalValue;
